import { BusinessCustomerTypesEnum } from '../screens/NewQuotation/CustomerDataPage/customerDataPage.model';
import { IntersectEnums } from '../services/common/miscUtils.types';
import { CustomerTypeEnum } from './customerTypeSelection';
import { ProductFamilyEnum } from './productFamilySelection';

/**
 * - Country numeric code according to ISO-3166.
 * - Hardcoded as Ireland.
 * - It's not the same as CountryCodeType in BUS models.
 * - In UI we show this enum as mix of country Alpha-2 code and Numeric code according to ISO-3166 (IE 353).
 */
export enum CountryCodeEnum {
  Ireland = '+353',
  Poland = '+48',
  Germany = '+49',
}

export enum CorrespondenceMethodEnum {
  email = 'email',
  post = 'post',
}

export enum TelephoneTypeEnum {
  landline = 'Landline',
  mobile = 'Mobile',
}

/**
 * - Values are both display text and submission values - no translation needed.
 */
export enum EntityFormEnum {
  ltd = 'Private company limited by shares (LTD company)',
  dacShares = 'Designated activity company (DAC) - (limited by shares)',
  dacGuarantee = 'Designated activity company limited by guarantee (DAC) -(limited by guarantee)',
  clg = 'Company limited by guarantee (CLG) (limited by guarantee, not having a share capital)',
  plc = 'Public limited company (PLC)',
  singleMember = 'Single member company',
  unlimited = 'Unlimited company',
  specialFund = 'Special fund',
  ucits = 'Undertakings for collective investment in transferable securities (UCITS)',
  eeig = 'European economic interest groupings (EEIG)',
  se = 'Societas Europaea (SE)',
  indAndProvSociety = 'Industrial and Provident Society',
  friendlySociety = 'Friendly society',
  tradeUnion = 'Trade union',
  llp = 'Limited liability partnership (LLP)',
  otherRoi = 'Other legal form (ROI)',
  otherNonRoi = 'Other legal form (non ROI)',
  partnership = 'Partnership',
  club = 'Club',
  association = 'Association',
  charity = 'Unincorporated charity',
  otherNonLegal = 'Other non legal form',
}

export enum InstitutionalTypeEnum {
  'type1' = '1 Non-financial corporations ',
  'type2' = '2 Deposit-taking corporations other than credit institutions ',
  'type3' = '3 Money market funds (MMF) ',
  'type4' = '4 Non-MMF investment funds ',
  'type5' = '5 Financial vehicle corporations (FVCs) engaged in securitisation transactions ',
  'type6' = '6 Other financial intermediaries, except: insurance corporations, pension funds and financial vehicle corporations engaged in securitisation transactions ',
  'type7' = '7 Financial auxiliaries ',
  'type8' = '8 Captive financial institutions and money lenders ',
  'type9' = '9 Insurance corporations ',
  'type10' = '10 Pension funds ',
  'type11' = '11 Social security funds ',
  'type12' = '12 Non-profit institutions serving households ',
  'type13' = '13 Households other than sole proprietors ',
  'type14' = '14 Sole proprietorships and partnerships without legal status',
}

export enum EnterpriseSizeEnum {
  micro = 'Micro',
  small = 'Small',
  medium = 'Medium',
  large = 'Large',
}

export enum NoYesEnum {
  no = 'no',
  yes = 'yes',
}

export enum ProductType {
  'hirePurchase' = 'hirePurchase',
  'hirePurchaseBalloon' = 'hirePurchaseBalloon',
  'pcp' = 'pcp',
  'servicePlan' = 'servicePlan',
}

export enum PaymentType {
  'Monthly' = 'Monthly',
  'Upfront' = 'Upfront',
}

/**
 * - Application flow type describing certain key properties of current journey.
 * - Current journey more than one value of the enum can be valid.
 * e.g. your flow can be 'business', 'ltdPdc' and 'servicePlan' at the same time.
 */
export enum FlowTypeEnum {
  /* Entries of CustomerTypeEnum */
  business = 'business',
  consumer = 'consumer',
  /* Entries of BusinessCustomerTypesEnum */
  ltd = 'ltd',
  plc = 'plc',
  soleTrader = 'soleTrader',
  partnership = 'partnership',
  other = 'other',
  unlimited = 'unlimited',
  /* Entries of ProductFamilyEnum */
  servicePlan = 'servicePlan',
  financialProduct = 'financialProduct',
  operatingLease = 'operatingLease',
  privateLease = 'privateLease',
}

FlowTypeEnum satisfies IntersectEnums<
  [typeof CustomerTypeEnum, typeof BusinessCustomerTypesEnum, typeof ProductFamilyEnum]
>;

export enum CreditCheckStatusEnum {
  manualUnderwriting = 'manualUnderwriting',
  moreInformationRequired = 'moreInformationRequired',
  conditionallyApproved = 'conditionallyApproved',
  approved = 'approved',
  declined = 'declined',
  submitted = 'submitted',
}

export enum TitleEnum {
  mr = 'mr',
  mrs = 'mrs',
  miss = 'miss',
  ms = 'ms',
  dr = 'dr',
  professor = 'professor',
  father = 'father',
  notApplicable = 'notApplicable',
}

export enum MaritalStatusEnum {
  married = 'married',
  single = 'single',
  coHabiting = 'coHabiting',
  divorced = 'divorced',
  separated = 'separated',
  widowed = 'widowed',
  widower = 'widower',
}

export enum IncomeFrequencyEnum {
  weekly = 'Weekly',
  monthly = 'Monthly',
  biMonthly = 'Bi-Monthly',
  annually = 'Annually',
}

export enum GenderEnum {
  male = 'male',
  female = 'female',
}

export enum CcrTypeEnum {
  /**
   * Personal Public Service (PPS) Number
   */
  ppsn = 'PPSN',
  itnNonRoi = 'Individual Tax Number (non ROI)',
}

export enum ResidentialStatusEnum {
  owner = 'owner',
  privateTenant = 'privateTenant',
  livingWithParents = 'livingWithParents',
}

export enum CompanyClassificationEnum {
  sme = 'sme',
  largeFleet = 'largeFleet',
  international = 'international',
}

export enum IdentityTypeEnum {
  cro = 'cro',
  nonTaxIdentity = 'nonTaxIdentity',
}
